import { useMemo, useState } from 'react';
import { useAttributeValues } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import {
  ATTRIBUTE_CONDITION_TYPE,
  AttributeRow as SharedAttributeRow,
  PEOPLE_SEARCH_OPERATOR_FAMILIES,
  ProfileAttributeNamePicker,
  peopleFilterBuilder,
  peopleGroupsForFamilies,
  useProfileAttributeNames,
} from '@/features/dryad';
import { ConditionTypeSelect } from './condition-type-select';

type AttributeRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
};

/**
 * The people surface's attribute row: the shared row and the shared
 * profile-attribute name picker, with the value suggestions this surface's
 * redaction permission gates.
 */
export function AttributeRow({ id }: AttributeRowProps) {
  const workspaceId = requireWorkspaceId();
  const meta = peopleFilterBuilder.useMeta();
  const { state } = peopleFilterBuilder.useLeafState(id, ATTRIBUTE_CONDITION_TYPE);

  // "Select an attribute" renders only once the picker has been visited
  // (closing its dropdown) — or from the start when the row mounted with a
  // name already chosen — so a freshly added row doesn't open covered in red.
  const [fieldTouched, setFieldTouched] = useState(() => state.field !== '');

  const names = useProfileAttributeNames(state.field);

  const isDisabled = meta.isDisabled || !meta.canViewAttributeValues;

  const values = useAttributeValues(workspaceId, meta.canViewAttributeValues ? state.field : '');
  const valueOptions = useMemo(() => (values.data ?? []).map((value) => ({ value, label: value })), [values.data]);

  return (
    <SharedAttributeRow
      id={id}
      conditionType={ATTRIBUTE_CONDITION_TYPE}
      operatorGroups={(field) => peopleGroupsForFamilies(PEOPLE_SEARCH_OPERATOR_FAMILIES, field)}
      typeSelect={<ConditionTypeSelect nodeId={id} currentType={ATTRIBUTE_CONDITION_TYPE} />}
      isDisabled={isDisabled}
      valueOptions={valueOptions}
      isLoadingValueOptions={values.isFetching}
      isFieldTouched={fieldTouched}
      onFieldTouched={() => setFieldTouched(true)}
      renderNamePicker={(pickerProps) => <ProfileAttributeNamePicker {...pickerProps} names={names} />}
    />
  );
}
